import { useState, useMemo } from 'react';
import { toast } from 'react-toastify';
import ReactSelect from 'react-select';
import { matchSorter } from 'match-sorter';

import useUpdateTinyAccount from 'services/account/useUpdateTinyAccount';
import useActivePractitioners from 'services/general/practitionersList';
import { ActivePractitioner } from 'types/PractitionerUser';
import TinyAccount from 'types/TinyAccount';

import { ButtonSizes, DesignButton, Spinfinity, Typography } from 'components';

import styles from './AccountModal.module.css';

type PractitionerOption = {
  label: string;
  value: string;
  organization?: string;
};

const emptyPractitionerOption: PractitionerOption = {
  label: 'No practitioner',
  value: '',
  organization: '',
};

type PractitionerFormProps = {
  tinyAccount: TinyAccount;
  practitionerId?: string | null;
  onFinishCallback?: (practitioner?: ActivePractitioner) => void;
};

export const AddPractitionerForm = ({
  tinyAccount,
  practitionerId,
  onFinishCallback,
}: PractitionerFormProps) => {
  const tinyAccountMutation = useUpdateTinyAccount();
  const { data } = useActivePractitioners();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedValue, setSelectedValue] = useState(
    practitionerId ? practitionerId : '',
  );
  const [inputValue, setInputValue] = useState('');
  const selectOptions = useMemo(() => {
    if (data?.length) {
      const practitionerOptions = data.map(practitioner => ({
        label: practitioner.name,
        value: practitioner.id,
        organization: practitioner.organization ?? '',
      }));
      return [emptyPractitionerOption, ...practitionerOptions];
    }
    return [emptyPractitionerOption];
  }, [data]);
  const filteredOptions = useMemo(() => {
    if (inputValue) {
      const sorted = matchSorter(selectOptions, inputValue, {
        keys: ['organization', 'label'],
      });
      return sorted;
    }
    return selectOptions;
  }, [inputValue, selectOptions]);

  const submitMainAccountUpdateForm = async () => {
    setIsSubmitting(true);
    const selectedPractitionerId = selectedValue?.trim();
    try {
      await tinyAccountMutation.mutateAsync({
        body: {
          practitioner_id: selectedPractitionerId || null,
        },
        tinyAccountId: tinyAccount.id,
      });
      toast.success('Practitioner updated');
    } catch (error) {
      toast.error('Your request could not be completed. Please try again');
    }
    setIsSubmitting(false);
    if (onFinishCallback) {
      onFinishCallback(
        data?.find(practitioner => practitioner.id === selectedPractitionerId),
      );
    }
  };

  if (!selectOptions || selectOptions.length <= 1) return <Spinfinity />;
  return (
    <form
      onSubmit={e => {
        e.preventDefault();
        submitMainAccountUpdateForm();
      }}
    >
      <ReactSelect
        options={filteredOptions}
        onChange={option =>
          setSelectedValue(
            option?.value ? option.value : emptyPractitionerOption.value,
          )
        }
        defaultValue={selectOptions.find(
          option => option?.value === practitionerId,
        )}
        isSearchable={true}
        components={{
          Option: ({ children, ...props }) => (
            <div {...props} onClick={props.innerProps.onClick}>
              <div className={styles.selectOption}>
                {children}
                <p className='mb-0 f7'>{props.data.organization}</p>
              </div>
            </div>
          ),
        }}
        filterOption={() => true}
        onInputChange={inputValue => setInputValue(inputValue)}
      />
      <Typography variant='body-s' className={styles.modalAgreement}>
        By clicking save you agree to give your practitioner access to your
        results, survey answers and action plan for all family members.
      </Typography>
      <DesignButton
        label='Save'
        type='submit'
        loading={isSubmitting}
        size={ButtonSizes.M}
        width={'full'}
        className='mt-4'
      />
    </form>
  );
};
